import { ChevronRight } from 'lucide-react';

/**
 * Shared event context header for payment modals, Next Steps, and other CTA surfaces.
 * 
 * Universal rule: Event title is ALWAYS clickable when eventId is available.
 * Matches the Chat pattern (underlined, navigates to /events/:id).
 * 
 * Props:
 *   eventId       - UUID of the event (enables clickable title)
 *   eventName     - Display name (e.g. "Birthday Party")
 *   vendorName    - e.g. "Party Props Plus"
 *   vendorService - e.g. "Balloon decorations"
 *   eventDate     - ISO date string
 *   location      - e.g. "New Orleans, LA"
 *   onNavigate    - Called before navigation (e.g. to close a modal)
 *   showViewEvent - Show explicit "View Event >" link below details (default: false)
 */
export const EventContextHeader = ({
  eventId,
  eventName,
  vendorName,
  vendorService,
  eventDate,
  location,
  onNavigate,
  showViewEvent = false,
}) => {
  const canNavigate = !!eventId;

  const handleNav = (e) => {
    e.stopPropagation();
    if (!canNavigate) return;
    onNavigate?.();
    window.location.href = `/events/${eventId}`;
  };

  const formattedDate = eventDate
    ? new Date(eventDate + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const details = [vendorName, vendorService, formattedDate, location?.split(',')[0]?.trim()]
    .filter(Boolean)
    .join(' \u00B7 ');

  return (
    <div className="mt-2 text-sm" data-testid="event-context-header">
      {eventName && (
        canNavigate ? (
          <button
            onClick={handleNav}
            className="font-medium text-gray-800 underline decoration-gray-300 underline-offset-2 hover:decoration-[#C6A96B] hover:text-[#1C1C1C] transition-colors text-left"
            data-testid="event-context-title-link"
          >
            {eventName}
          </button>
        ) : (
          <p className="font-medium text-gray-800" data-testid="event-context-title">
            {eventName}
          </p>
        )
      )}
      {details && (
        <p className="text-gray-500 text-xs mt-0.5">{details}</p>
      )}
      {showViewEvent && canNavigate && (
        <button
          onClick={handleNav}
          className="mt-1.5 inline-flex items-center gap-1 text-xs text-[#C6A96B] hover:text-[#B89655] font-medium transition-colors"
          data-testid="event-context-view-event"
        >
          View Event <ChevronRight className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};
